import React, { useEffect, useState } from 'react';

const getUrgencyColor = (urgency) => {
  switch (urgency?.toLowerCase()) {
    case 'critical': return '#ff4d4f';
    case 'moderate': return '#faad14';
    case 'low': return '#52c41a';
    default: return '#ccc';
  }
};

const toWords = (d) => {
  if (!d) return '';
  if (Array.isArray(d)) return d.join(' ').toLowerCase();
  if (typeof d === 'string') return d.toLowerCase();
  if (typeof d === 'object') {
    const label = d.label || d.name;
    return (label ? String(label) : String(d)).toLowerCase();
  }
  return String(d).toLowerCase();
};

const unitEmojiSafe = (dispatch) => {
  const v = toWords(dispatch);
  if (v.includes('police') || v.includes('law')) return '🚓';
  if (v.includes('ambulance') || v.includes('emt') || v.includes('medic') || v.includes('paramedic')) return '🚑';
  if (v.includes('fire') || v.includes('rescue')) return '🚒';
  return '🚨';
};

const getDisplayCode = (incident) => {
  if (incident?.incidentCode) return incident.incidentCode;
  if (incident?.code) return incident.code;
  if (incident?.id) return incident.id.slice(-6).toUpperCase();
  return 'UNKNOWN';
};

const getSecondsAgo = (incident) => {
  const t =
    (incident?.createdAt?.seconds ? incident.createdAt.seconds * 1000 : null) ??
    (incident?.timestamp ? Date.parse(incident.timestamp) : null);
  if (!t) return '';
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff > 1200) return 'Over 20 min';
  return `${diff} seconds ago`;
};

const IncidentCard = ({ incident, onAccept, onViewDetails, isArchived }) => {
  const urgencyColor = getUrgencyColor(incident.urgency);
  const emoji = unitEmojiSafe(incident.dispatch || incident.type);
  const secondsAgo = getSecondsAgo(incident);
  const code = getDisplayCode(incident);

  const [dispatchStatus, setDispatchStatus] = useState('Automatically Dispatching...');

  useEffect(() => {
    if (isArchived) return;
    const steps = ['Automatically Dispatching...', 'Auto-Dispatched', 'EMTs Arrived', 'Threat Neutralized'];
    let idx = 0;
    const iv = setInterval(() => {
      idx += 1;
      if (idx < steps.length) setDispatchStatus(steps[idx]);
      else clearInterval(iv);
    }, 911000); // 9m11s
    return () => clearInterval(iv);
  }, [isArchived]);

  const units = Array.isArray(incident.dispatch)
    ? incident.dispatch
    : incident.dispatch
    ? [incident.dispatch]
    : [];

  return (
    <div
      style={{
        borderLeft: `6px solid ${urgencyColor}`,
        borderRadius: '12px',
        padding: '0.9rem 1rem',
        marginBottom: '1rem',
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
      }}
    >
      {/* Top row */}
      <div className="card-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h4 className="card-title">{emoji} {incident.title}</h4>
        <div className="card-meta" style={{ display: 'flex', gap: 8 }}>
          {incident.assignedTo === 'ai' && (
            <span style={{ background: '#ede9fe', color: '#7c3aed', padding: '2px 6px', borderRadius: 6, fontSize: 12 }}>
              AI
            </span>
          )}
          {incident.isDuplicate && (
            <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 6, fontSize: 12 }}>
              Duplicate
            </span>
          )}
          <span className="meta-pill">#{code}</span>
          <span className="meta-time">{secondsAgo}</span>
        </div>
      </div>

      <p style={{ margin: 0 }}>
        <strong>Urgency:</strong> <span style={{ color: urgencyColor }}>{incident.urgency}</span>
      </p>
      <p style={{ margin: '0.25rem 0' }}>
        <strong>Location:</strong>{' '}
        {typeof incident.location === 'object'
          ? incident.location.name || 'Unknown'
          : incident.location || 'Unknown'}
      </p>

      {!isArchived && (
        <div className="auto-dispatching" style={{ marginTop: '0.5rem' }}>
          {emoji} <span className="dispatch-label">{dispatchStatus}</span>{' '}
          {units.length > 0 ? `— ${units.join(', ')}` : ''}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button
          onClick={onViewDetails}
          style={{
            padding: '0.4rem 0.75rem',
            backgroundColor: '#e9ecef',
            color: '#212529',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '0.85rem',
          }}
        >
          View details
        </button>

        {!isArchived && (
          <button
            onClick={onAccept}
            style={{
              padding: '0.4rem 0.75rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            Accept
          </button>
        )}
      </div>
    </div>
  );
};

export default IncidentCard;
